import { Combobox, Transition } from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/20/solid";
import { map } from "lodash";
import { Fragment, useState, useEffect } from "react";

export default function ReportUploadDropdown({
  data,
  placeHolder,
  selected,
  setSelected,
  width,
  value,
  defaultValue,
  inputWidth,
  documentType,
  allDocuments,
  /** When true (e.g. backend Manage Assessment), do not enforce sequential report-type locks so tests can upload any type. */
  allowFlexibleReportTypes = false,
}) {
  const [query, setQuery] = useState("");

  // Set the default selected item to the one with primary: true or fallback to defaultValue
  useEffect(() => {
    if (!selected) {
      const defaultItem = data?.find((item) => item.primary) || defaultValue;
      if (defaultItem && defaultItem !== selected) {
        setSelected(defaultItem);
      }
    }
  }, [data, defaultValue, selected, setSelected]);

  const filteredData =
    query === ""
      ? data
      : data?.filter((item) =>
          (item.name || item.email)
            ?.toLowerCase()
            .replace(/\s+/g, "")
            .includes(query.toLowerCase().replace(/\s+/g, ""))
        );

  const getDisplayValue = (item) => item?.name || "";

  // Logic to disable options
  const isOptionDisabled = (type) => {
    // When documentType is Report Published to Library, only Sponsor Observation Report is enabled
    if (documentType === "Report Published to Library") {
      return type !== "Sponsor Observation Report";
    }

    if (allowFlexibleReportTypes) {
      return false;
    }

    const documentTypes =
      allDocuments?.items?.map((doc) => doc?.document_sub_type) || [];
    const docStatus =
      allDocuments?.items?.map((doc) => doc?.document_status) || [];
    // if (docStatus.includes("Published")) {
    //   return [
    //     "Sponsor Draft Report",
    //     "Vendor Draft Report",
    //     "Final Report",
    //     "Report Anonymized",
    //   ].includes(type);
    // }

    if (documentTypes.includes("Report Anonymized")) {
      return [""].includes(type);
    } else if (documentTypes.includes("Final Report")) {
      return [""].includes(type);
    } else if (documentTypes.includes("Sponsor Observation Report")) {
      return ["Report Anonymized"].includes(type);
    } else if (documentTypes.includes("Vendor Draft Report")) {
      return ["Report Anonymized"].includes(type);
    } else if (documentTypes.includes("Sponsor Draft Report")) {
      return ["Report Anonymized"].includes(type);
    } else if (documentTypes.length === 0) {
      return [
        "Vendor Draft Report",
        "Sponsor Observation Report",
        "Final Report",
        "Report Anonymized",
      ].includes(type);
    } else {
      return [
        "Vendor Draft Report",
        "Sponsor Observation Report",
        "Final Report",
        "Report Anonymized",
      ].includes(type);
    }
  };

  return (
    <div className="">
      <Combobox value={selected} onChange={setSelected}>
        <div className="relative">
          <div
            className={`relative w-full lg:${width} xl:${width} cursor-default overflow-hidden rounded-lg bg-white text-left border border-gray-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/75 focus-visible:ring-offset-2 focus-visible:ring-offset-teal-300 sm:text-sm`}
          >
            <Combobox.Input
              className="w-full border-none py-2 pl-3 pr-10 text-sm leading-5 text-gray-900 focus:ring-0"
              displayValue={getDisplayValue}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={placeHolder}
              defaultValue={defaultValue}
              style={{ minWidth: "220px" }} // Ensure input is wide enough for full text
            />
            <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronUpDownIcon
                className="h-5 w-5 text-gray-400"
                aria-hidden="true"
              />
            </Combobox.Button>
          </div>
          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            afterLeave={() => setQuery("")}
          >
            <Combobox.Options
              className={`z-50 absolute mt-1 max-h-60 ${inputWidth} overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm`}
            >
              {filteredData?.length === 0 && query !== "" ? (
                <div className="relative cursor-default select-none px-4 py-2 text-gray-700">
                  Nothing found.
                </div>
              ) : (
                filteredData?.map((item) => (
                  <Combobox.Option
                    key={item?.id}
                    disabled={isOptionDisabled(item.name)}
                    className={({ active, disabled }) =>
                      `relative cursor-default select-none py-2 pl-5 pr-4 ${
                        active
                          ? "bg-custom-link-bg-color text-white"
                          : disabled
                          ? "text-gray-400 cursor-not-allowed"
                          : "text-gray-900"
                      }`
                    }
                    value={item}
                  >
                    {({ selected, active, disabled }) => (
                      <span
                        className={`block truncate ${
                          selected ? "font-medium" : "font-normal"
                        }`}
                      >
                        {item.name}
                      </span>
                    )}
                  </Combobox.Option>
                ))
              )}
            </Combobox.Options>
          </Transition>
        </div>
      </Combobox>
    </div>
  );
}
